import { Colors } from "@/components/shared/util";
import { Patient } from "@/store/modules/patient/types";
import _ from "lodash";
import SVG from "svg.js";
import { Store } from "vuex";
import { BodyConfig } from ".";
import { PartBase } from "./part";

export class InfoWindow extends PartBase {
  padding = 5;
  lineHeight = 13;
  textHeight = 11;
  shiftX = 0;
  shiftY = 0;
  linesToBold = [0];
  container: SVG.Container;
  svgGroup: SVG.G;
  textContainer: SVG.G;
  rect?: SVG.Rect;
  lineObjects: Array<SVG.Text> = [];
  linesToColor: Array<{ color: string; index: number }> = [];

  constructor(config: BodyConfig, container: SVG.Container, store: Store<any>) {
    super(config, container, store);
    this.container = container;

    const group = this.container as SVG.G;
    this.svgGroup = group.group();
    this.textContainer = group.group();

    this.rect = this.svgGroup.rect().attr({ class: "InfoWindow" });
  }

  shiftContent() {
    this.svgGroup.move(this.shiftX, this.shiftY);
    this.textContainer.move(this.shiftX, this.shiftY);
  }

  updateData(patient: Patient | null) {
    super.updateData(patient);
    if (!this.showing) {
      this.textContainer.clear();
      return;
    }
    if (patient) {
      this.getExtraData(patient);

      if (!this.extraData) {
        return;
      }

      const dataToDisplay =
        this.extraData.tableData ||
        Object.values(this.extraData).filter((value: any) => {
          return value;
        });

      // Hide empty box and avoid drawing table
      const noTableDataEntries =
        this.extraData.tableData && this.extraData.tableData.length === 0;

      if (noTableDataEntries || dataToDisplay.length === 0) {
        this.svgGroup.hide();
        return;
      } else {
        this.svgGroup.show();
      }

      if (!this.supportsForeignObject) {
        const lines: Array<string> = [];

        dataToDisplay.forEach((value: any) => {
          let row;
          if (value.rows) {
            if (_.isArray(value.rows)) {
              row = value.rows.join(" ");
            } else {
              row = value.rows;
            }
          } else {
            row = value;
          }
          row.split("|").forEach((line: string) => lines.push(line));
        });

        this.createText(lines);
        this.colorLines();
        this.boldLines();
        this.resizeBox();
      } else {
        this.textContainer.clear();
        const fobj = this.textContainer!.foreignObject(0, 0);

        fobj.appendChild("div", { innerHTML: this.htmlTable });
        const div = fobj.getChild(0);

        const padding = 3;
        const border = 1;
        div.className = "InfoWindowDiv";
        div.style.padding = `${padding}px`;

        const width = div.scrollWidth + padding + border * 2;
        const height = div.scrollHeight + border * 2;
        fobj.width(width);
        fobj.height(height);
        this.rect!.size(width, height);
      }
    }
  }

  private get htmlTable() {
    let table = `<table>`;
    const data = this.extraData.tableData || Object.values(this.extraData);

    data
      .filter((d: any) => d)
      .forEach((d: any, i: number) => {
        let row;
        if (d.rows) {
          row = d.rows;
        } else {
          row = d;
        }
        if (_.isArray(row)) {
          let rowStyle = "";
          let bulletHtml = "";
          let color = "";
          let bulletStyle = "font-size: 11px;font-weight:900;";
          if (d.color) {
            rowStyle = `style="font-weight:bold"`;
            color = `color: ${d.color};`;
          } else {
            bulletStyle += `visibility: hidden`;
          }

          let arrowStyle = "";
          let iconType = "arrow_upward";
          if (d.level && d.level < 3) {
            //arrowStyle = "transform: rotate(180deg); position: static;vertical-align: sub;"; // TODO: WT-1443 Safari renders the arrow icon to the left of the body panel when using 'trasnform: rortate(90)'
            iconType = "arrow_downward";
          }

          bulletHtml = `<i class="v-icon icon-bullet material-icons" style="${arrowStyle}${bulletStyle}${color}${arrowStyle}">${iconType}</i>`;

          const multiRow = row[1].split("|");
          row[1] = multiRow.splice(0, 1);
          const centerPadding = `style="padding-left:10px"`;
          table += `<tr><td ${rowStyle}>${bulletHtml}${row[0]}</td>
          <td ${rowStyle} ${centerPadding}></td>
          <td ${rowStyle}>${row[1]}</td></tr>`;
          multiRow.forEach((line: string) => {
            table += `<tr ${rowStyle}><td></td>
            <td ${centerPadding}></td>
            <td>${line}</td></tr>`;
          });
        } else if (_.isString(row)) {
          const len: number = row.split("|").length;

          row.split("|").forEach(part => {
            const styleToAdd = [];

            if (i > 0) {
              styleToAdd.push("padding-top: 2px;");
            }
            if (this.linesToBold.includes(i)) {
              styleToAdd.push("font-weight: bold");
            }
            if (this.linesToColor[i]) {
              styleToAdd.push(`color: ${this.linesToColor[i].color}`);
            }

            let styles = "";
            if (styleToAdd) {
              styles = `style="${styleToAdd.join(";")}"`;
            }
            table += `<tr><td ${styles} colspan="3">${part}</td></tr>`;
          });
        }
      });
    return table + "</table>";
  }

  get supportsForeignObject() {
    return typeof SVGForeignObjectElement !== "undefined";
  }

  getClickTarget() {
    return this.supportsForeignObject ? this.textContainer : this.svgGroup;
  }

  createText(lines: Array<string>) {
    const container = this.textContainer as SVG.Container;
    let accumulator = this.padding;
    this.lineObjects.forEach(lineObject => {
      lineObject.remove();
    });
    this.lineObjects = [];

    const color = this.dark ? Colors.White : Colors.Black;

    lines.forEach(line => {
      const text = container
        .text(line)
        .font({
          size: this.textHeight * this.globalConfig.textScale
        })
        .fill({ color })
        .attr("pointer-events", "none")
        .move(this.padding, accumulator);
      accumulator += this.lineHeight * this.globalConfig.textScale;
      this.lineObjects.push(text);
    });
  }

  updateBrightnessMode() {
    super.updateBrightnessMode();
    if (!this.supportsForeignObject) {
      const color = this.dark ? Colors.White : Colors.Black;

      this.lineObjects.forEach(line => {
        line.fill({ color });
      });
    }
  }

  private resizeBox() {
    const textContainerDimentions = this.textContainer!.bbox();
    this.rect!.size(
      textContainerDimentions.w + this.padding * 2,
      textContainerDimentions.h + this.padding * 2
    );
  }

  boldLines() {
    this.linesToBold.forEach((line: number) => {
      if (this.lineObjects[line]) {
        this.lineObjects[line].font({ weight: "bold" });
      }
    });
  }

  colorLines() {
    this.linesToColor.forEach(config => {
      const line = this.lineObjects[config.index];
      if (line) {
        line.fill(config.color);
      }
    });
  }
}
